// ─── Leverancier-categorieen (type leverancier) + voorkeursleverancier 1/2 ────
//
// Beheerbare lijst van leverancier-typen (Gereedschap, Kleding & PBM, Telefonie
// & internet, ...). Een leverancier (Client type=LEVERANCIER) kan in meerdere
// categorieen zitten; per categorie optioneel een voorkeursleverancier 1 en 2.
// De categorie stuurt de leverancier-suggestie bij een offerteaanvraag. De
// Zod-schemas zijn de ene bron voor API en formulier.

import { z } from 'zod'
import { idParam } from './id-validators'

export interface LeverancierRefDTO {
  id: string
  name: string
}

export interface LeverancierCategorieDTO {
  id: string
  naam: string
  omschrijving: string | null
  sortering: number
  actief: boolean
  voorkeur1: LeverancierRefDTO | null
  voorkeur2: LeverancierRefDTO | null
  /** Aantal gekoppelde (niet-verwijderde) leveranciers. */
  aantalLeveranciers: number
}

/** Categorie zoals hij aan een leverancier hangt (lijst + relatie-detail). */
export interface LeverancierCategorieTagDTO {
  id: string
  naam: string
  /** 1 of 2 als deze leverancier voorkeursleverancier is in de categorie. */
  voorkeurRang: 1 | 2 | null
}

export const leverancierCategorieCreateSchema = z
  .object({
    naam: z.string().trim().min(1, 'Naam is verplicht').max(120),
    omschrijving: z.string().trim().max(500).optional().nullable(),
    sortering: z.number().int().min(0).max(10000).optional(),
    actief: z.boolean().optional(),
    voorkeur1LeverancierId: idParam().optional().nullable(),
    voorkeur2LeverancierId: idParam().optional().nullable(),
  })
  .refine(
    (v) => !v.voorkeur1LeverancierId || v.voorkeur1LeverancierId !== v.voorkeur2LeverancierId,
    { message: 'Voorkeur 1 en 2 moeten verschillende leveranciers zijn', path: ['voorkeur2LeverancierId'] },
  )
export type LeverancierCategorieCreateInput = z.infer<typeof leverancierCategorieCreateSchema>

export const leverancierCategorieUpdateSchema = z
  .object({
    naam: z.string().trim().min(1, 'Naam is verplicht').max(120).optional(),
    omschrijving: z.string().trim().max(500).optional().nullable(),
    sortering: z.number().int().min(0).max(10000).optional(),
    actief: z.boolean().optional(),
    voorkeur1LeverancierId: idParam().optional().nullable(),
    voorkeur2LeverancierId: idParam().optional().nullable(),
  })
  .refine(
    (v) => !v.voorkeur1LeverancierId || v.voorkeur1LeverancierId !== v.voorkeur2LeverancierId,
    { message: 'Voorkeur 1 en 2 moeten verschillende leveranciers zijn', path: ['voorkeur2LeverancierId'] },
  )
export type LeverancierCategorieUpdateInput = z.infer<typeof leverancierCategorieUpdateSchema>

/** Set-semantiek: de volledige lijst categorieen van een leverancier. */
export const leverancierCategorieenZetSchema = z.object({
  categorieIds: z.array(idParam()).max(50),
})
export type LeverancierCategorieenZetInput = z.infer<typeof leverancierCategorieenZetSchema>
